import { useState } from "react"
import { FloatingLabel, Form, Button } from "react-bootstrap"
import { consoleLog, safetyPercentage, colorCode } from "../utilities/helpers"
import { FLAGS_API_URL } from "../const/paths"
import { Link } from "react-router-dom"
import "./EditCommunitySettingsModal.css"

export function EditCommunitySettingsModal({ onClose, onSubmit, community }) {
    const [communityName, setCommunityName] = useState(community.name || "")
    const [chiefUpdates, setChiefUpdates] = useState({})

    /**
     * IMPORTANT: ID Mapping
     *
     * Members in community.persons/places have TWO important IDs:
     * - `id`: The chaperee table record ID (used for API updates like DELETE/PUT)
     * - `member_id`: The actual User ID (if person) or Entity ID (if place)
     *
     * The community.chiefs array uses `id` fields that correspond to member_id,
     * NOT the chaperee id. So when checking roles or updating chiefs:
     *
     * ✅ Correct:   chiefs.find(c => c.id === member.member_id)
     * ❌ Wrong:     chiefs.find(c => c.id === member.id)
     *
     * See docs/community-api.md "ID Mapping and Relationships" for details.
     */

    // Get all members (people and places combined)
    const allMembers = [
        ...(community.persons || []),
        ...(community.places || []),
    ].sort((a, b) => {
        if (a.pinned && !b.pinned) return -1
        if (!a.pinned && b.pinned) return 1
        const orderA =
            a.order !== undefined && a.order !== null
                ? a.order
                : Number.MAX_SAFE_INTEGER
        const orderB =
            b.order !== undefined && b.order !== null
                ? b.order
                : Number.MAX_SAFE_INTEGER
        if (orderA !== orderB) return orderA - orderB
        if (a.updated_at && b.updated_at) {
            return new Date(a.updated_at) - new Date(b.updated_at)
        }
        return a.id - b.id
    })

    // Parse current chiefs from community
    let currentChiefs = []
    if (community.chiefs) {
        currentChiefs =
            typeof community.chiefs === "string"
                ? JSON.parse(community.chiefs)
                : community.chiefs
    }

    const getChiefRole = (memberId) => {
        if (!currentChiefs || !Array.isArray(currentChiefs)) {
            return null
        }
        const chief = currentChiefs.find(
            (c) => String(c.id) === String(memberId),
        )
        // Anyone with creator, admin, or chief role counts as a chief
        if (
            chief &&
            (chief.role === "creator" ||
                chief.role === "admin" ||
                chief.role === "chief")
        ) {
            return "chief"
        }
        return null
    }

    const handleChiefRoleChange = (memberId, newRole) => {
        setChiefUpdates((prev) => ({
            ...prev,
            [memberId]: newRole,
        }))
    }

    const handleSubmit = async (event) => {
        event.preventDefault()

        if (!communityName.trim()) {
            console.error("Community name cannot be empty")
            return
        }

        const updatedData = {
            communityId: community.id,
            name: communityName,
            chiefUpdates: chiefUpdates,
        }

        consoleLog("Updating community settings:", updatedData)

        try {
            if (onSubmit) {
                await onSubmit(updatedData)
            }
            onClose()
        } catch (error) {
            console.error("Error updating community settings:", error)
        }
    }

    return (
        <div className="modal-content">
            <div className="modal-header mb-3">
                <h4>
                    <i className="bi bi-gear pe-2"></i>
                    Community Settings
                </h4>
            </div>
            <div className="modal-body p-3">
                <form onSubmit={handleSubmit}>
                    {/* Community Name Input */}
                    <div className="mb-4">
                        <FloatingLabel
                            controlId="communityNameInput"
                            label="Community Name"
                        >
                            <Form.Control
                                type="text"
                                placeholder="Enter community name"
                                value={communityName}
                                onChange={(e) =>
                                    setCommunityName(e.target.value)
                                }
                                maxLength="100"
                                required
                            />
                        </FloatingLabel>
                    </div>

                    <hr />

                    {/* Members List */}
                    <div className="mb-4">
                        <h6 className="fw-bold mb-3">Community Members</h6>
                        {allMembers.length === 0 ? (
                            <p className="text-muted small">
                                No members in this community yet.
                            </p>
                        ) : (
                            <ul className="list-group list-group-flush">
                                {allMembers.map((item) => (
                                    <li
                                        key={item.id}
                                        className="list-group-item d-flex justify-content-between align-items-start px-0 py-2"
                                    >
                                        <div
                                            className="d-flex align-items-start"
                                            style={{ flex: "1", minWidth: "0" }}
                                        >
                                            {item.type === "person" ? (
                                                <div
                                                    style={{
                                                        flex: 1,
                                                        minWidth: 0,
                                                        display: "flex",
                                                        flexDirection: "column",
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            alignItems:
                                                                "center",
                                                            textAlign: "left",
                                                        }}
                                                    >
                                                        {item.profile_picture_url ? (
                                                            <img
                                                                src={
                                                                    item.profile_picture_url
                                                                }
                                                                alt={
                                                                    item.username
                                                                }
                                                                className={`flag-avatar flag-avatar-sm border-${colorCode(safetyPercentage(item.overall_safety), "class")} me-2`}
                                                                style={{
                                                                    flexShrink: 0,
                                                                }}
                                                            />
                                                        ) : (
                                                            <i
                                                                className={`border-${colorCode(safetyPercentage(item.overall_safety), "class")} bi bi-person-circle me-2 text-muted text-${colorCode(safetyPercentage(item.overall_safety), "class")}`}
                                                                style={{
                                                                    fontSize:
                                                                        "1.5rem",
                                                                    flexShrink: 0,
                                                                }}
                                                            ></i>
                                                        )}
                                                        <span
                                                            style={{
                                                                wordBreak:
                                                                    "break-word",
                                                            }}
                                                        >
                                                            {item?.full_name ||
                                                                item?.username ||
                                                                item?.email}
                                                        </span>
                                                    </div>
                                                    {item.entity_name && (
                                                        <div
                                                            className="small text-muted mt-1 ms-5"
                                                            style={{
                                                                textAlign:
                                                                    "left",
                                                            }}
                                                        >
                                                            <Link
                                                                to={`/analysis/${item.slug}`}
                                                                className={`text-decoration-none text-${colorCode(safetyPercentage(item.overall_safety))}`}
                                                            >
                                                                <img
                                                                    src={`${FLAGS_API_URL}/${item.country_code}.svg`}
                                                                    alt={
                                                                        item.entity_name
                                                                    }
                                                                    style={{
                                                                        width: "20px",
                                                                        height: "auto",
                                                                        marginRight:
                                                                            "4px",
                                                                    }}
                                                                />
                                                                {
                                                                    item.entity_name
                                                                }
                                                            </Link>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div
                                                    style={{
                                                        flex: 1,
                                                        minWidth: 0,
                                                        display: "flex",
                                                        alignItems:
                                                            "flex-start",
                                                    }}
                                                >
                                                    <Link
                                                        to={`/analysis/${item.slug}`}
                                                        className="text-decoration-none text-secondary"
                                                        style={{
                                                            wordWrap:
                                                                "break-word",
                                                            overflowWrap:
                                                                "break-word",
                                                            minWidth: "0",
                                                            display: "flex",
                                                            alignItems:
                                                                "flex-start",
                                                        }}
                                                    >
                                                        <img
                                                            src={`${FLAGS_API_URL}/${item?.country_code}.svg`}
                                                            data-bs-toggle="tooltip"
                                                            data-bs-title={
                                                                item?.fromGoogle
                                                                    ?.name ||
                                                                item?.name
                                                            }
                                                            alt={
                                                                item?.fromGoogle
                                                                    ?.name ||
                                                                item?.name
                                                            }
                                                            className={`flag-avatar flag-avatar-md border-${colorCode(safetyPercentage(item?.overall_safety), "class")}`}
                                                            style={{
                                                                marginRight:
                                                                    "8px",
                                                                cursor: "pointer",
                                                                flexShrink: "0",
                                                            }}
                                                        />
                                                        <span
                                                            style={{
                                                                wordBreak:
                                                                    "break-word",
                                                            }}
                                                        >
                                                            {item?.fromGoogle
                                                                ?.name ||
                                                                item?.name}
                                                        </span>
                                                    </Link>
                                                </div>
                                            )}
                                        </div>

                                        {/* Chief Role Select for People Only */}
                                        {item.type === "person" && (
                                            <div
                                                className="ms-2"
                                                style={{ minWidth: "120px" }}
                                            >
                                                <Form.Select
                                                    size="sm"
                                                    value={
                                                        chiefUpdates[
                                                            item.member_id
                                                        ] !== undefined
                                                            ? chiefUpdates[
                                                                  item.member_id
                                                              ]
                                                            : getChiefRole(
                                                                  item.member_id,
                                                              ) || "citizen"
                                                    }
                                                    onChange={(e) =>
                                                        handleChiefRoleChange(
                                                            item.member_id,
                                                            e.target.value,
                                                        )
                                                    }
                                                >
                                                    <option value="citizen">
                                                        Citizen
                                                    </option>
                                                    <option value="chief">
                                                        Chief
                                                    </option>
                                                </Form.Select>
                                            </div>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {/* Submit Buttons */}
                    <div className="d-flex gap-2 justify-content-end">
                        <Button variant="dark" type="submit">
                            <i className="bi bi-check-circle me-2"></i>
                            Save Changes
                        </Button>
                        <Button variant="outline-secondary" onClick={onClose}>
                            Cancel
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    )
}
